import React, { FC } from 'react';
import styled from 'styled-components';
import {
    SkeletonBlock,
    SkeletonFiltersRow,
    SkeletonFooterBar,
    SkeletonPageHeader,
    SkeletonPageShell,
    SkeletonRow,
    SkeletonStack,
    SkeletonSurface,
    SkeletonTable,
    SkeletonTableRow,
    SkeletonTabs
} from 'src/components/widgets/skeleton';

const ProofingShell = styled(SkeletonPageShell).attrs({ $padding: '20px 24px 48px' })`
    background: #f6f8fb;
`;

const ProofingHeaderActionsSkeleton = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
`;

const ProofingToolbarSkeleton = styled(SkeletonSurface).attrs({ $padding: '12px', $radius: '8px' })`
    display: grid;
    gap: 10px;
`;

const ProofingBoardSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(270px, 1fr));
    gap: 10px;
    min-width: 1240px;
`;

const ProofingBoardScrollSkeleton = styled.div`
    overflow: auto;
`;

const ProofingBoardColumnSkeleton = styled(SkeletonSurface).attrs({ $padding: '12px', $radius: '8px' })`
    display: grid;
    gap: 10px;
    align-content: start;
`;

const ProofingBoardCardsSkeleton = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
`;

const ProofingCardSkeleton = styled(SkeletonSurface).attrs({ $padding: '8px', $radius: '8px' })`
    display: grid;
    gap: 8px;
    min-width: 0;
`;

const ProofingDetailsContentSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(260px, 0.75fr) minmax(0, 1.25fr);
    gap: 14px;
    width: min(100%, 1180px);
    margin: 0 auto;
    min-width: 0;

    @media (max-width: 960px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const ProofingDetailsPanelSkeleton = styled(SkeletonSurface).attrs({ $padding: '14px', $radius: '8px' })`
    display: grid;
    gap: 14px;
`;

const ProofingWorkflowLayoutSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 280px;
    gap: 14px;
    min-width: 0;

    @media (max-width: 960px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const WorkflowDefinitionsToolbarSkeleton = styled.div`
    display: grid;
    grid-template-columns: minmax(240px, 1fr) 180px auto;
    gap: 12px;
    align-items: end;

    @media (max-width: 760px) {
        grid-template-columns: minmax(0, 1fr);
    }
`;

const ProofingTableSkeleton: FC<{ rows?: number }> = ({ rows = 8 }) => (
    <SkeletonTable>
        <SkeletonTableRow
            $columns='minmax(180px, 1.4fr) minmax(110px, 0.6fr) minmax(120px, 0.75fr) minmax(120px, 0.75fr) 80px'
            $mobileColumns='minmax(0, 1fr) 80px'
            $mobileBreakpoint='860px'
            $minHeight='42px'
        >
            <SkeletonBlock $width='84px' $height='13px' />
            <SkeletonBlock $width='56px' $height='13px' />
            <SkeletonBlock $width='72px' $height='13px' />
            <SkeletonBlock $width='86px' $height='13px' />
            <SkeletonBlock $width='1px' $height='1px' />
        </SkeletonTableRow>
        {Array.from({ length: rows }).map((_, index) => (
            <SkeletonTableRow
                key={index}
                $columns='minmax(180px, 1.4fr) minmax(110px, 0.6fr) minmax(120px, 0.75fr) minmax(120px, 0.75fr) 80px'
                $mobileColumns='minmax(0, 1fr) 80px'
                $mobileBreakpoint='860px'
            >
                <SkeletonStack $gap='8px'>
                    <SkeletonBlock $width={index % 2 === 0 ? 'min(280px, 100%)' : 'min(210px, 100%)'} $height='15px' />
                    <SkeletonBlock $width='min(180px, 100%)' $height='12px' />
                </SkeletonStack>
                <SkeletonBlock $width='92px' $height='22px' $radius='11px' />
                <SkeletonBlock $width={index % 2 === 0 ? '112px' : '82px'} $height='14px' />
                <SkeletonBlock $width={index % 3 === 0 ? '96px' : '124px'} $height='14px' />
                <SkeletonRow $gap='8px' $justify='flex-end'>
                    <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                    <SkeletonBlock $width='28px' $height='28px' $radius='4px' />
                </SkeletonRow>
            </SkeletonTableRow>
        ))}
    </SkeletonTable>
);

export const ProofListPageSkeleton: FC = () => (
    <ProofingShell aria-hidden='true'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(220px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
            </SkeletonStack>
            <ProofingHeaderActionsSkeleton>
                <SkeletonBlock $width='128px' $height='36px' $radius='6px' />
                <SkeletonBlock $width='36px' $height='36px' $radius='6px' />
            </ProofingHeaderActionsSkeleton>
        </SkeletonPageHeader>
        <SkeletonStack $gap='12px'>
            <ProofingToolbarSkeleton>
                <SkeletonFiltersRow
                    searchWidth='260px'
                    controlWidths={['150px', '180px', '150px', '120px']}
                    controlLabelWidths={['44px', '58px', '48px', '42px']}
                />
                <SkeletonTabs tabWidths={['64px', '74px', '86px']} $margin='0' />
            </ProofingToolbarSkeleton>
            <ProofingBoardScrollSkeleton>
                <ProofingBoardSkeleton>
                    {Array.from({ length: 4 }).map((_, columnIndex) => (
                        <ProofingBoardColumnSkeleton key={columnIndex}>
                            <SkeletonRow $justify='space-between'>
                                <SkeletonBlock $width={columnIndex % 2 === 0 ? '92px' : '118px'} $height='15px' />
                                <SkeletonBlock $width='28px' $height='20px' $radius='10px' />
                            </SkeletonRow>
                            <ProofingBoardCardsSkeleton>
                                {Array.from({ length: columnIndex === 0 ? 4 : 3 }).map((_, cardIndex) => (
                                    <ProofingCardSkeleton key={cardIndex}>
                                        <SkeletonBlock $width={cardIndex % 2 === 0 ? 'min(130px, 100%)' : 'min(96px, 100%)'} $height='15px' />
                                        <SkeletonBlock $width='min(92px, 76%)' $height='58px' $radius='6px' />
                                        <SkeletonBlock $width='72px' $height='12px' />
                                        <SkeletonRow $gap='6px'>
                                            <SkeletonBlock $width='42px' $height='14px' />
                                            <SkeletonBlock $width='30px' $height='14px' />
                                        </SkeletonRow>
                                    </ProofingCardSkeleton>
                                ))}
                            </ProofingBoardCardsSkeleton>
                        </ProofingBoardColumnSkeleton>
                    ))}
                </ProofingBoardSkeleton>
            </ProofingBoardScrollSkeleton>
        </SkeletonStack>
    </ProofingShell>
);

export const ProofDetailsPageSkeleton: FC = () => (
    <ProofingShell aria-hidden='true'>
        <ProofingDetailsPanelSkeleton>
            <SkeletonRow $gap='8px'>
                <SkeletonBlock $width='84px' $height='12px' />
                <SkeletonBlock $width='120px' $height='12px' />
            </SkeletonRow>
            <SkeletonPageHeader $margin='0'>
                <SkeletonStack $gap='8px' $flex='1'>
                    <SkeletonBlock $width='min(360px, 100%)' $height='28px' />
                    <SkeletonRow $gap='8px'>
                        <SkeletonBlock $width='86px' $height='22px' $radius='11px' />
                        <SkeletonBlock $width='112px' $height='22px' $radius='11px' />
                    </SkeletonRow>
                </SkeletonStack>
                <ProofingHeaderActionsSkeleton>
                    <SkeletonBlock $width='116px' $height='36px' $radius='6px' />
                    <SkeletonBlock $width='36px' $height='36px' $radius='6px' />
                </ProofingHeaderActionsSkeleton>
            </SkeletonPageHeader>
            <SkeletonTabs tabWidths={['96px', '82px', '110px', '86px']} $margin='0' />
        </ProofingDetailsPanelSkeleton>
        <ProofingDetailsContentSkeleton>
            <ProofingDetailsPanelSkeleton>
                <SkeletonBlock $width='130px' $height='18px' />
                {Array.from({ length: 3 }).map((_, index) => (
                    <SkeletonSurface key={index} $padding='12px' $radius='8px'>
                        <SkeletonRow $gap='12px'>
                            <SkeletonBlock $width='64px' $height='64px' $radius='8px' />
                            <SkeletonStack $gap='8px' $flex='1'>
                                <SkeletonBlock $width={index % 2 === 0 ? 'min(190px, 100%)' : 'min(150px, 100%)'} $height='15px' />
                                <SkeletonBlock $width='min(130px, 100%)' $height='12px' />
                            </SkeletonStack>
                        </SkeletonRow>
                    </SkeletonSurface>
                ))}
            </ProofingDetailsPanelSkeleton>
            <ProofingWorkflowLayoutSkeleton>
                <ProofingDetailsPanelSkeleton>
                    <SkeletonBlock $width='150px' $height='18px' />
                    {Array.from({ length: 4 }).map((_, index) => (
                        <SkeletonSurface key={index} $padding='14px' $radius='8px'>
                            <SkeletonStack $gap='10px'>
                                <SkeletonRow $justify='space-between'>
                                    <SkeletonBlock $width={index % 2 === 0 ? 'min(220px, 100%)' : 'min(170px, 100%)'} $height='16px' />
                                    <SkeletonBlock $width='84px' $height='22px' $radius='11px' />
                                </SkeletonRow>
                                <SkeletonBlock $height='10px' $radius='6px' />
                                <SkeletonRow $gap='6px'>
                                    <SkeletonBlock $width='28px' $height='28px' $round />
                                    <SkeletonBlock $width='28px' $height='28px' $round />
                                    <SkeletonBlock $width='28px' $height='28px' $round />
                                </SkeletonRow>
                            </SkeletonStack>
                        </SkeletonSurface>
                    ))}
                </ProofingDetailsPanelSkeleton>
                <ProofingDetailsPanelSkeleton>
                    <SkeletonBlock $width='128px' $height='18px' />
                    <SkeletonBlock $height='36px' $radius='6px' />
                    <SkeletonBlock $height='82px' $radius='6px' />
                    <SkeletonBlock $height='36px' $radius='6px' />
                </ProofingDetailsPanelSkeleton>
            </ProofingWorkflowLayoutSkeleton>
        </ProofingDetailsContentSkeleton>
        <SkeletonFooterBar>
            <SkeletonBlock $width='88px' $height='36px' $radius='6px' />
            <SkeletonBlock $width='74px' $height='36px' $radius='6px' />
        </SkeletonFooterBar>
    </ProofingShell>
);

export const ProofWorkflowDefinitionsPageSkeleton: FC = () => (
    <SkeletonPageShell aria-hidden='true' $padding='24px 24px 48px'>
        <SkeletonPageHeader>
            <SkeletonStack $gap='8px' $flex='1'>
                <SkeletonBlock $width='min(300px, 100%)' $height='26px' />
                <SkeletonBlock $width='min(520px, 100%)' $height='13px' />
            </SkeletonStack>
            <SkeletonBlock $width='128px' $height='36px' $radius='6px' />
        </SkeletonPageHeader>
        <SkeletonStack $gap='20px'>
            <WorkflowDefinitionsToolbarSkeleton>
                <SkeletonBlock $height='38px' $radius='6px' />
                <SkeletonBlock $height='38px' $radius='6px' />
                <SkeletonBlock $width='92px' $height='36px' $radius='6px' />
            </WorkflowDefinitionsToolbarSkeleton>
            <ProofingTableSkeleton rows={7} />
        </SkeletonStack>
    </SkeletonPageShell>
);
